import { Component, Input, OnInit } from '@angular/core';

import { AppService } from '../../services/app.service';
import { FinalPaperDocumentService } from '../../services/final-paper-document.service';
import { ITableColumn } from '../../shared/table/table.component';
import { IFinalPaperDocument, IFinalPaperDocumentFile } from '../../models/FinalPaperDocument';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { FinalPaperDocumentFilesComponent } from './final-paper-document-files.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { store } from './store';

@Component({
    selector: 'app-final-paper-document-list',
    templateUrl: './final-paper-document-list.component.html'
})
export class FinalPaperDocumentListComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: FinalPaperDocumentService,
        private modal: NgbModal
    ) { }

    @Input() type: 'protocol' | 'order';

    private _academicYearId: string;

    get academicYearId() {
        return this._academicYearId;
    }

    @Input() set academicYearId(value: string) {
        this._academicYearId = value;
        this.filter();
    }

    readonly columns: ITableColumn[] = [
        { property: 'AcademicYearName', label: 'finalPaperDocument_lblAcademicYear', sorts: true },
        { property: 'Name', label: 'finalPaperDocument_lblName', sorts: true },
        { property: 'Type', label: 'finalPaperDocument_lblType', sorts: true },
        { property: 'RegNumber', label: 'finalPaperDocument_lblRegNumber', sorts: true },
        { width: '1px' }
    ];

    items: IFinalPaperDocument[] = [];
    searchValue: string;
    selectedYear: 'all' | 'selected' = 'all';
    selectedType: string;
    showAll: boolean;

    readonly options: {
        year: { text: string, value: string }[],
        type: { text: string, value: string }[]
    } = {
            year: [
                { text: this.app.translate('finalPaperDocument_yearTypeAll'), value: 'all' },
                { text: this.app.translate('finalPaperDocument_yearTypeSelected'), value: 'selected' }
            ],
            type: []
        };

    private allItems: IFinalPaperDocument[] = [];
    private readonly files: { [documentId: number]: IFinalPaperDocumentFile[] } = {};
    private readonly searchSubj = new Subject<string>();

    ngOnInit() {
        let request: Observable<IFinalPaperDocument[]>;

        const upn = store.getPerson()?.Upn;

        switch (this.type) {
            case 'order': request = this.service.getOrders(upn); break;
            case 'protocol': request = this.service.getProtocols(upn); break;
        }

        if (!request) return;

        if (this.academicYearId) {
            this.selectedYear = 'selected';
        }

        this.searchSubj.pipe(debounceTime(300), distinctUntilChanged()).subscribe(term => {
            this.filter();
        });

        this.app.addLoading(request).subscribe(data => {
            this.items = this.allItems = data;

            const years: string[] = [];
            const types: { text: string, value: string }[] = [];

            this.items.forEach(t => {
                if (!years.includes(t.AcademicYear)) years.push(t.AcademicYear);

                if (!types.some(n => n.value == t.Type)) {
                    types.push({
                        text: t.Type,
                        value: t.Type
                    });
                }
            });

            this.options.type = types;

            if (this.academicYearId) {
                this.filter();
            }
        });
    }

    search() {
        this.searchSubj.next(this.searchValue);
    }

    filter() {
        if (this.filterEmpty()) {
            this.items = this.allItems;
        } else {
            const sv = this.searchValue?.toLowerCase();

            this.items = this.allItems.filter(t => {
                if (this.selectedType && t.Type != this.selectedType) return false;
                if (this.selectedYear == 'selected' && t.AcademicYearId != this.academicYearId) return false;

                if (sv) {
                    return (t.AcademicYear.toLowerCase().includes(sv)
                        || t.Name.toLowerCase().includes(sv)
                        || t.RegNumber.toLowerCase().includes(sv)
                        || t.Type.toLowerCase().includes(sv)
                    );
                }

                return true;
            });
        }
    }

    open(document: IFinalPaperDocument) {
        this.getFiles(document).subscribe(files => {
            const rows = files.map(t => {
                return {
                    name: t.Name,
                    url: this.service.getFileDownloadUrl(document.Id, t.Id, store.getPerson()?.Upn)
                };
            });

            const ref = this.modal.open(FinalPaperDocumentFilesComponent);
            ref.componentInstance.title = document.Name;
            ref.componentInstance.files = rows;
            ref.result.then(() => { }, () => { });
        });
    }

    private getFiles(document: IFinalPaperDocument) {
        if (this.files[document.Id]) {
            return of(this.files[document.Id]);
        }

        return this.app.addLoading(this.service.getFiles(document.Id, store.getPerson()?.Upn)).pipe(tap(files => {
            this.files[document.Id] = files;
        }));
    }

    private filterEmpty() {
        return !this.searchValue && !this.selectedType && !this.selectedYear;
    }
}
